import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { AdminIcon } from "@/components/admin/icon";
import { AuthBrand } from "@/components/auth/auth-brand";
import {
	extractErrorMessage,
	useResetPasswordMutation,
} from "@/hooks/use-auth-api";

const schema = z
	.object({
		password: z.string().min(12, "12 caractères minimum"),
		confirm: z.string(),
	})
	.refine((d) => d.password === d.confirm, {
		message: "Les deux mots de passe ne correspondent pas",
		path: ["confirm"],
	});

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const token = params.get("token");
	const mutation = useResetPasswordMutation();
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: { password: "", confirm: "" },
	});

	const newPwd = watch("password");
	const score = scorePassword(newPwd);

	const submit = handleSubmit(async (data) => {
		if (!token) {
			setError("Lien invalide : token manquant");
			return;
		}
		setError(null);
		try {
			await mutation.mutateAsync({ token, password: data.password });
			setSuccess(true);
			setTimeout(() => navigate("/login"), 2000);
		} catch (err) {
			setError(extractErrorMessage(err, "Réinitialisation impossible"));
		}
	});

	if (!token) {
		return (
			<div className="auth-page">
				<AuthBrand />
				<main className="auth-panel">
					<div className="auth-form-wrap">
						<div className="auth-alert danger" data-testid="reset-no-token">
							<span className="icon">
								<AdminIcon n="alert" s={16} />
							</span>
							<div>
								<strong>Lien invalide</strong>
								Le token est manquant. Demandez un nouveau lien depuis « Mot de
								passe oublié ».
							</div>
						</div>
						<button
							type="button"
							className="btn-auth-ghost"
							onClick={() => navigate("/login/mot-de-passe-oublie")}
						>
							<AdminIcon n="chevL" s={14} /> Nouveau lien
						</button>
					</div>
				</main>
			</div>
		);
	}

	return (
		<div className="auth-page">
			<AuthBrand />
			<main className="auth-panel">
				<div className="auth-form-wrap">
					<div className="auth-h">
						<h2>Choisissez un nouveau mot de passe</h2>
						<p>
							Toutes vos sessions actives seront déconnectées par mesure de
							sécurité.
						</p>
					</div>

					{success && (
						<div className="auth-alert success" data-testid="reset-success">
							<span className="icon">
								<AdminIcon n="check" s={16} />
							</span>
							<div>
								<strong>Mot de passe réinitialisé</strong>
								Redirection vers la page de connexion…
							</div>
						</div>
					)}

					{error && (
						<div className="auth-alert danger" data-testid="reset-error">
							<span className="icon">
								<AdminIcon n="alert" s={16} />
							</span>
							<div>{error}</div>
						</div>
					)}

					{!success && (
						<form onSubmit={submit} noValidate>
							<div className="field">
								<div className="field-label">Nouveau mot de passe</div>
								<div
									className={
										"field-input-wrap " + (errors.password ? "error" : "")
									}
								>
									<span className="input-prefix">
										<AdminIcon n="lock" s={16} c="var(--a-ink-mute)" />
									</span>
									<input
										type="password"
										placeholder="Minimum 12 caractères"
										autoComplete="new-password"
										data-testid="reset-pwd"
										{...register("password")}
									/>
								</div>
								{newPwd && (
									<div className="pwd-meter" style={{ marginTop: 8 }}>
										<div className={score >= 1 ? "l1" : ""} />
										<div className={score >= 3 ? "l2" : ""} />
										<div className={score >= 5 ? "l3" : ""} />
									</div>
								)}
								{errors.password && (
									<div className="field-help error">
										<AdminIcon n="alert" s={12} />
										{errors.password.message}
									</div>
								)}
							</div>

							<div className="field">
								<div className="field-label">Confirmer</div>
								<div
									className={
										"field-input-wrap " + (errors.confirm ? "error" : "")
									}
								>
									<span className="input-prefix">
										<AdminIcon n="lock" s={16} c="var(--a-ink-mute)" />
									</span>
									<input
										type="password"
										autoComplete="new-password"
										data-testid="reset-pwd-confirm"
										{...register("confirm")}
									/>
								</div>
								{errors.confirm && (
									<div className="field-help error">
										<AdminIcon n="alert" s={12} />
										{errors.confirm.message}
									</div>
								)}
							</div>

							<button
								type="submit"
								className="btn-submit"
								disabled={isSubmitting}
								data-testid="reset-submit"
							>
								{isSubmitting
									? "Réinitialisation…"
									: "Définir le nouveau mot de passe"}
							</button>
						</form>
					)}
				</div>
			</main>
		</div>
	);
}

function scorePassword(p: string): number {
	if (!p) return 0;
	let s = 0;
	if (p.length >= 12) s++;
	if (/[A-Z]/.test(p)) s++;
	if (/[a-z]/.test(p)) s++;
	if (/\d/.test(p)) s++;
	if (/[!@#$%&*\-_+=?]/.test(p)) s++;
	return s;
}
